import { Breadcrumb, TitleScreen } from "components/Core"
import { getCountryByTimezone } from "helpers/country_helper"
import { getTimezoneFromLocalStorage } from "helpers/datetime_helper"
import React, { useContext, useEffect, useState, useRef } from "react"
import { createPortal } from "react-dom"
import { Button } from "flowbite-react"
import SelectedFiltersDisplay from "overrides/theme/omnix/base/components/Map/SelectedFiltersDisplay"
import GoToCoordinatesButton from "overrides/theme/omnix/base/components/Map/GoToCoordinatesButton"
import { AppContext } from "store/context/AppContext"
import { LocationsContext } from "store/context/LocationsContext"
import { Filter, Shapes, X, Check, AlertTriangle, AlertCircle, Pencil } from "lucide-react"
import { useMapStore } from "store/mapStore"
import MapComponent from "overrides/theme/omnix/base/components/Map/GoogleMap"
import MapLoading from "overrides/theme/omnix/base/components/Map/MapLoading"
import MapError from "overrides/theme/omnix/base/components/Map/MapError"
import { Wrapper, Status } from "@googlemaps/react-wrapper"
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_VERSION } from "config/maps"
import GlossaryModal from "overrides/theme/omnix/base/components/Map/GlossaryModal"
import { PolygonForm } from "overrides/theme/omnix/base/components/Map/PolygonForm"

const MapModal = ({ content, onClose }: { content: any; onClose: () => void }) => {
  const {
    title,
    text,
    icon,
    confirmButtonText,
    cancelButtonText,
    showCancelButton,
    onConfirm,
    onCancel,
    confirmButtonColor,
    timer,
  } = content

  useEffect(() => {
    if (timer) {
      const timeout = setTimeout(() => {
        onConfirm ? onConfirm() : onClose()
      }, timer)
      return () => clearTimeout(timeout)
    }
  }, [timer, onConfirm, onClose])

  const getIcon = () => {
    switch (icon) {
      case "success":
        return (
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <Check className="h-6 w-6 text-green-600" />
          </div>
        )
      case "error":
        return (
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
        )
      case "warning":
        return (
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
            <AlertTriangle className="h-6 w-6 text-yellow-600" />
          </div>
        )
      default:
        return null
    }
  }

  return (
    <div
      className="bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:p-6 border border-gray-200"
      style={{ pointerEvents: "auto", width: "400px" }}
    >
      <div>
        {getIcon()}
        <div className="mt-3 text-center sm:mt-5">
          <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
            {title}
          </h3>
          <div className="mt-2">
            <p className="text-sm text-gray-500" dangerouslySetInnerHTML={{ __html: text }} />
          </div>
        </div>
      </div>
      <div
        className={`mt-5 sm:mt-6 ${showCancelButton ? "sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense" : ""}`}
      >
        <button
          type="button"
          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:col-start-2 sm:text-sm custom-confirm-btn"
          style={{ backgroundColor: confirmButtonColor || "#2563eb" }}
          onClick={onConfirm}
        >
          {confirmButtonText || "OK"}
        </button>
        {showCancelButton && (
          <button
            type="button"
            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
            onClick={onCancel}
          >
            {cancelButtonText || "Cancelar"}
          </button>
        )}
      </div>
    </div>
  )
}

const CoveragesMapScreen: React.FC = () => {
  const { open_table_filters, set_open_table_filters } = useContext(AppContext)
  const { initializeCountryCoordinates } = useMapStore()
  const {
    communes,
    selectedRegion,
    setSelectedRegion,
    selectedCommune,
    setSelectedCommune,
    setSelectedPolygon,
    selectedPolygon,
    selectedPolygonType,
    setSelectedPolygonType,
    selectedCouriers,
    setSelectedCouriers,
    refreshPolygons,
    communeGeoError,
    setDisplayedPolygons,
    polygonsRefreshTrigger,
    displayedPolygons,
    communeCoordinates,
    setIsLoadingPolygons,
  } = useContext(LocationsContext)

  console.log(selectedPolygonType)

  const [mapAlert, setMapAlert] = useState<any>(null)

  const hasActiveFilters = [selectedRegion, selectedCommune, selectedPolygon, selectedCouriers?.length].some(
    (val) => !!val,
  )

  const handleClearFilters = () => {
    setSelectedRegion(null)
    setSelectedCommune(null)
    setSelectedPolygonType("all")
    setSelectedCouriers([])
    setSelectedPolygon(null)
  }

  // Helper for map-integrated alerts
  const showMapAlert = (options: any) => {
    return new Promise<{ isConfirmed: boolean }>((resolve) => {
      setMapAlert({
        ...options,
        onConfirm: () => {
          setMapAlert(null)
          resolve({ isConfirmed: true })
        },
        onCancel: () => {
          setMapAlert(null)
          resolve({ isConfirmed: false })
        },
      })
    })
  }

  // Close any alert when commune or polygon changes
  useEffect(() => {
    setMapAlert(null)
  }, [selectedCommune, selectedPolygon])

  // Reset selected polygon when courier changes - MOVED TO HANDLER to avoid loops
  // useEffect removed

  // Ref to track previous commune to only show alert on commune change
  const prevCommuneRef = useRef<string | null>(null)

  // Handle geographic information error alert
  useEffect(() => {
    if (communeGeoError && selectedCommune) {
      showMapAlert({
        title: "Información",
        text: communeGeoError,
        icon: "warning",
        confirmButtonColor: "#3B82F6",
        confirmButtonText: "Entendido",
      })
    }
  }, [communeGeoError, selectedCommune])

  // Paint polygons automatically when commune or couriers changes
  useEffect(() => {
    let ignore = false

    const fetchAndPaintPolygons = async () => {
      // If no commune selected and no couriers selected, clear and reset ref
      if (!selectedCommune && (!selectedCouriers || selectedCouriers.length === 0)) {
        setDisplayedPolygons([])
        prevCommuneRef.current = null
        return
      }

      try {
        const userString = localStorage.getItem("USER_SIMO")
        const token = userString ? JSON.parse(userString).token : null
        if (!token || ignore) return

        const { fetchPolygons } = await import("services/polygon_services")
        const courierIds = selectedCouriers?.length > 0 ? selectedCouriers.map((c: any) => c.id) : null
        setIsLoadingPolygons(true)
        const { records, totalRecordsMatched } = await fetchPolygons(courierIds, token, "all", selectedCommune)
        setIsLoadingPolygons(false)

        if (ignore) return

        if (records && records.length > 0) {
          setDisplayedPolygons(records)

          // Only show alert if the commune has actually changed
          if (selectedCommune && selectedCommune !== prevCommuneRef.current) {
            setTimeout(async () => {
              if (ignore) return
              await showMapAlert({
                title: selectedCommune || "Polígonos Cargados",
                text: `Se han cargado <strong>${totalRecordsMatched}</strong> polígonos para la comuna seleccionada.`,
                icon: "success",
                confirmButtonColor: "#3B82F6",
                timer: 3000,
              })
            }, 500)
          }
        } else {
          // If records is empty or null, we just clear the map.
          // The geo alert is now handled by communeGeoError in its own useEffect
          setDisplayedPolygons([])
        }

        // Update ref after processing
        prevCommuneRef.current = selectedCommune
      } catch (err) {
        console.error("Error auto-fetching polygons:", err)
      }
    }

    fetchAndPaintPolygons()

    return () => {
      ignore = true
    }
    // eslint-disable-next-line
  }, [selectedCommune, selectedCouriers, setDisplayedPolygons, polygonsRefreshTrigger])

  // No need for local filtering anymore as Context provides filtered polygons
  const filteredPolygons = displayedPolygons

  const timezone = getTimezoneFromLocalStorage()
  const country = getCountryByTimezone(timezone)
  const [isGlossaryOpen, setIsGlossaryOpen] = useState(false)

  const [showPolygonForm, setShowPolygonForm] = useState(false)
  const [tempPolygon, setTempPolygon] = useState<any>(null)
  const [mapReady, setMapReady] = useState(false)
  console.log(mapReady)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isDrawing, setIsDrawing] = useState(false) // Drawing state
  const [overlayStyle, setOverlayStyle] = useState<React.CSSProperties>({
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 999999,
    pointerEvents: "none",
  })

  useEffect(() => {
    const updateOverlayPosition = () => {
      // Check for fullscreen (standard or webkit)
      const isFullscreenDoc = !!document.fullscreenElement || !!(document as any).webkitFullscreenElement
      setIsFullscreen(isFullscreenDoc)

      let newStyle: React.CSSProperties

      if (isFullscreenDoc) {
        newStyle = {
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 999999,
          pointerEvents: "none",
        }
      } else {
        const mapContainer = document.getElementById("map-container")
        if (mapContainer) {
          const rect = mapContainer.getBoundingClientRect()
          newStyle = {
            position: "fixed",
            top: `${rect.top}px`,
            left: `${rect.left}px`,
            width: `${rect.width}px`,
            height: `${rect.height}px`,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 999999,
            pointerEvents: "none",
          }
        } else {
          return // No map container, do nothing
        }
      }

      setOverlayStyle((prevStyle) => {
        // Deep compare assuming simple style object
        if (JSON.stringify(prevStyle) === JSON.stringify(newStyle)) {
          return prevStyle
        }
        return newStyle
      })
    }

    // Listeners for layout changes
    window.addEventListener("resize", updateOverlayPosition)
    window.addEventListener("scroll", updateOverlayPosition, true) // Capture scroll
    document.addEventListener("fullscreenchange", updateOverlayPosition)
    document.addEventListener("webkitfullscreenchange", updateOverlayPosition)

    // Initial call
    updateOverlayPosition()
    // Also interval to catch animation changes?
    const interval = setInterval(updateOverlayPosition, 500)

    return () => {
      window.removeEventListener("resize", updateOverlayPosition)
      window.removeEventListener("scroll", updateOverlayPosition, true)
      document.removeEventListener("fullscreenchange", updateOverlayPosition)
      document.removeEventListener("webkitfullscreenchange", updateOverlayPosition)
      clearInterval(interval)
    }
  }, [])

  useEffect(() => {
    initializeCountryCoordinates(country)
  }, [country, initializeCountryCoordinates])

  const handleConvertCommune = async () => {
    console.log("handleConvertCommune called. Selected Commune:", selectedCommune)
    if (!selectedCommune) {
      showMapAlert({
        title: "Atención",
        text: "Debe seleccionar una comuna para realizar esta acción",
        icon: "warning",
        confirmButtonColor: "#3B82F6",
      })
      return
    }

    // Use pre-fetched coordinates from context if available
    let area = communeCoordinates?.area
    console.log("Initial area check from context:", area)

    let communeObjFromList = null

    // If no area found in context, fallback to trying to find it in the local list
    if (!area) {
      communeObjFromList = communes.find((c: any) => (c?.name || c) === selectedCommune)
      area = communeObjFromList?.coordinates?.area
    }

    // If no area found locally, try to fetch it
    if (!area) {
      try {
        console.log("Commune details not found in context or local list, fetching for:", selectedCommune)
        // Get token from local storage
        const userString = localStorage.getItem("USER_SIMO")
        const token = userString ? JSON.parse(userString).token : null

        if (token) {
          const { fetchCommuneDetails } = await import("services/location_services")
          const details = await fetchCommuneDetails(selectedCommune, token)
          if (details && details.coordinates && details.coordinates.area) {
            area = details.coordinates.area
          }
        }
      } catch (err) {
        console.error("Error fetching commune details inside handler:", err)
      }
    }

    if (area) {
      let geometryCoordinates = area.coordinates
      if (area.type === "MultiPolygon") {
        geometryCoordinates = area.coordinates[0]
      }

      const newPolygon = {
        type: "include",
        status: true,
        coverageIds: [],
        polygon: {
          geometry: {
            coordinates: geometryCoordinates,
          },
        },
        coordinates: geometryCoordinates,
      }

      console.log("Setting tempPolygon and showing form...")
      setTempPolygon(newPolygon)
      setShowPolygonForm(true)
    } else if (communeObjFromList && communeObjFromList.polygon) {
      console.log("Setting tempPolygon from existing communeObjFromList.polygon")
      setTempPolygon(communeObjFromList.polygon)
      setShowPolygonForm(true)
    } else {
      console.warn("No polygon/area found for commune:", selectedCommune)
      showMapAlert({
        title: "Error",
        text: "No se pudieron obtener las coordenadas de la comuna seleccionada.",
        icon: "error",
      })
    }
  }

  const handleFormSubmit = async (data: any) => {
    if (tempPolygon) {
      // If the user manually typed new coordinates, parse and use them
      let polygonGeometry = tempPolygon.polygon
      let rootCoordinates = tempPolygon.coordinates

      if (data.coordinatesText && data.coordinatesText.trim() !== "") {
        try {
          const parsedRing: number[][] = JSON.parse(data.coordinatesText)

          // Ensure the ring is closed (first === last)
          const closedRing = [...parsedRing]
          if (
            closedRing.length > 0 &&
            (closedRing[0][0] !== closedRing[closedRing.length - 1][0] ||
              closedRing[0][1] !== closedRing[closedRing.length - 1][1])
          ) {
            closedRing.push([...closedRing[0]])
          }

          const geoJsonPaths = [closedRing] // Wrap in ring array: [[[lng, lat], ...]]

          polygonGeometry = {
            ...tempPolygon.polygon,
            geometry: {
              ...(tempPolygon.polygon?.geometry || {}),
              coordinates: geoJsonPaths,
            },
          }
          rootCoordinates = geoJsonPaths
        } catch (e) {
          console.error("Error parsing coordinatesText:", e)
        }
      }

      const finalPolygon = {
        ...tempPolygon,
        polygon: polygonGeometry,
        coordinates: rootCoordinates,
        id: data.id,
        type: data.type,
        status: data.status ? "ACTIVE" : "INACTIVE",
        coverageIds: data.selectedRegions || tempPolygon.coverageIds,
        courierIds: data.courierIds !== undefined ? data.courierIds : tempPolygon.courierIds,
        workingTime: data.workingTime !== undefined ? data.workingTime : tempPolygon.workingTime,
      }

      try {
        const userString = localStorage.getItem("USER_SIMO")
        const token = userString ? JSON.parse(userString).token : null
        if (!token) throw new Error("No token found")

        const { savePolygon } = await import("services/polygon_services")

        const { coordinates, ...rest } = finalPolygon
        const payload = {
          ...rest,
          status: !!data.status,
          type: data.type,
          courierIds: data.courierIds,
          workingTime: data.workingTime,
        }

        console.log("Saving polygon with payload:", payload)
        await savePolygon(payload, token)

        // Close form immediately so alert is not obstructed
        setShowPolygonForm(false)
        setTempPolygon(null)

        setSelectedPolygon(finalPolygon)

        // Optimistic update of local state to ensure color and filters update immediately
        setDisplayedPolygons((prev: any[]) => {
          const index = prev.findIndex((p) => p.id === finalPolygon.id)
          if (index > -1) {
            const newPolys = [...prev]
            newPolys[index] = finalPolygon
            return newPolys
          }
          return [...prev, finalPolygon]
        })

        refreshPolygons?.()

        await showMapAlert({
          title: "Éxito",
          text: "Polígono guardado correctamente",
          icon: "success",
          confirmButtonColor: "#3B82F6",
        })
      } catch (error) {
        console.error("Error saving polygon:", error)
        await showMapAlert({
          title: "Error",
          text: "No se pudo guardar el polígono. Por favor, intente nuevamente.",
          icon: "error",
          confirmButtonColor: "#3B82F6",
        })
      }
    }
  }

  const handleFormClose = () => {
    setShowPolygonForm(false)
    setTempPolygon(null)
  }

  const handleEditPolygon = (polygon: any) => {
    console.log("Editing polygon:", polygon)
    setTempPolygon(polygon)
    setShowPolygonForm(true)
  }

  const handlePolygonComplete = (paths: any[]) => {
    // Determine polygon type based on switch or default
    // We set default to 'include' for new polygons
    console.log("Polygon drawing complete:", paths)

    // Ensure polygon is closed (first point = last point) as required by backend
    const closedPaths = [...paths]
    if (closedPaths.length > 0) {
      const first = closedPaths[0]
      const last = closedPaths[closedPaths.length - 1]
      // Compare with small tolerance or strict equality? Strict for data integrity.
      if (first.lat !== last.lat || first.lng !== last.lng) {
        closedPaths.push(first)
      }
    }

    // Convert to GeoJSON format [lng, lat] for compatibility with MapComponent and Service
    const geoJsonPaths = closedPaths.map((p) => [p.lng, p.lat])

    const newPolygon = {
      // ID generated in Form
      type: "include",
      status: true,
      coverageIds: [],
      polygon: {
        geometry: {
          coordinates: [geoJsonPaths], // Nest as first ring for savePolygon to read coordinates[0]
        },
      },
      coordinates: geoJsonPaths, // Keep root alias if needed
    }

    setTempPolygon(newPolygon)
    setIsDrawing(false)
    setShowPolygonForm(true)
  }

  const handleDeletePolygon = async (id: string) => {
    const result = await showMapAlert({
      title: "Eliminar Área",
      text: "¿Está seguro que desea eliminar esta área? Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#6B7280",
    })

    if (result.isConfirmed) {
      try {
        const userString = localStorage.getItem("USER_SIMO")
        const token = userString ? JSON.parse(userString).token : null
        if (!token) throw new Error("No token found")

        const { deletePolygons } = await import("services/polygon_services")
        await deletePolygons([id], "Polygon deleted from Coverages Map", token)

        await showMapAlert({
          title: "Éxito",
          text: "La zona ha sido eliminada correctamente",
          icon: "success",
          confirmButtonColor: "#3B82F6",
        })

        setSelectedPolygon(null)
        refreshPolygons?.()
      } catch (error) {
        console.error("Error deleting polygon:", error)
        await showMapAlert({
          title: "Error",
          text: "No se pudo eliminar el área. Por favor, intente nuevamente.",
          icon: "error",
          confirmButtonColor: "#3B82F6",
        })
      }
    }
  }

  const handleSaveGeometry = async (id: string, newCoordinates: any) => {
    try {
      console.log("Saving new geometry for polygon:", id, newCoordinates)
      const userString = localStorage.getItem("USER_SIMO")
      const token = userString ? JSON.parse(userString).token : null
      if (!token) throw new Error("No token found")

      const { savePolygon } = await import("services/polygon_services")

      if (!selectedPolygon) return

      const payload = {
        ...selectedPolygon,
        status: !!selectedPolygon.status,
        type: selectedPolygon.type,
        polygon: {
          ...selectedPolygon.polygon,
          geometry: {
            ...selectedPolygon.polygon.geometry,
            coordinates: [newCoordinates.map((c: any) => [c.lng, c.lat])],
          },
        },
      }

      if ("coordinates" in payload) {
        delete payload.coordinates
      }

      await savePolygon(payload, token)

      await showMapAlert({
        title: "Éxito",
        text: "Geometría actualizada correctamente",
        icon: "success",
        // timer: 1500, // Custom modal simplified doesn't support timer out of box easily unless we add useEffect, skipped for now to ensure visibility
        showConfirmButton: true,
        confirmButtonText: "OK",
      })

      const updatedPolygon = {
        ...selectedPolygon,
        polygon: payload.polygon,
      }
      setSelectedPolygon(updatedPolygon)
      refreshPolygons?.()
    } catch (error) {
      console.error("Error saving geometry:", error)
      showMapAlert({
        title: "Error",
        text: "No se pudo actualizar la geometría.",
        icon: "error",
      })
    }
  }

  const render = (status: Status) => {
    switch (status) {
      case "LOADING":
        return <MapLoading />
      case "FAILURE":
        return <MapError />
      case "SUCCESS":
        return (
          <MapComponent
            onEdit={handleEditPolygon}
            onDelete={handleDeletePolygon}
            onSaveGeometry={handleSaveGeometry}
            onMapReady={() => setMapReady(true)}
            isDrawing={isDrawing}
            setIsDrawing={setIsDrawing}
            onPolygonComplete={handlePolygonComplete}
            polygons={filteredPolygons}
            setTempPolygon={setTempPolygon}
            setShowPolygonForm={setShowPolygonForm}
            tempPolygon={tempPolygon}
          />
        )
      default:
        return <></>
    }
  }

  // Helper to render portals
  const renderMapPortal = (child: React.ReactNode) => {
    const portalRoot = document.getElementById("map-portal-root")
    if (portalRoot) {
      return createPortal(
        <div style={overlayStyle}>
          <div style={{ pointerEvents: "auto" }}>{child}</div>
        </div>,
        portalRoot,
      )
    }
    return null
  }

  return (
    <div className="w-full h-full flex flex-col items-center justify-start">
      <Breadcrumb data={[{ name: "Administrar coberturas/zonas", href: "/coverages/map" }]} />
      <div className="w-full flex flex-col justify-center items-center py-4 px-6">
        <TitleScreen title={`Administrar coberturas/zonas - ${country}`} />
      </div>
      <div className="w-full flex flex-col gap-2 px-6">
        <div className="flex flex-col gap-2">
          <SelectedFiltersDisplay />
          {hasActiveFilters && (
            <div className="flex justify-start px-2 mb-2">
              <button
                onClick={handleClearFilters}
                className="text-[12px] font-bold text-[#007AFF] hover:text-[#F9004D] transition-colors flex items-center gap-1.5 underline underline-offset-4"
              >
                Limpiar filtros activos
              </button>
            </div>
          )}
        </div>
        <div className="w-full flex flex-col md:flex-row justify-between items-center gap-4 p-4 mb-4 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 transition-all">
          <div className="flex items-center gap-3">
            <GoToCoordinatesButton />
            <div className="h-8 w-px bg-gray-200 dark:bg-gray-700 mx-2" />
          </div>

          <div className="flex items-center gap-3">
            <Button
              color={isDrawing ? "failure" : "dark"}
              size="sm"
              onClick={() => setIsDrawing(!isDrawing)}
              // disabled={!selectedCommune}
              className={`transition-all duration-300 shadow-md hover:shadow-lg ${
                isDrawing
                  ? "!bg-red-600 hover:!bg-red-700 text-white"
                  : "!bg-indigo-600 hover:!bg-indigo-700 text-white"
              }`}
              title={isDrawing ? "Cancelar dibujo manual" : "Dibujar nuevo polígono manualmente"}
            >
              {isDrawing ? <X className="mr-2 h-4 w-4" /> : <Pencil className="mr-2 h-4 w-4" />}
              {isDrawing ? "Cancelar Dibujo" : "Dibujar Polígono"}
            </Button>

            <Button
              color="success"
              size="sm"
              onClick={handleConvertCommune}
              className={`transition-all duration-300 ${!selectedCommune ? "opacity-50 grayscale cursor-not-allowed" : "shadow-md hover:shadow-lg"}`}
              disabled={!selectedCommune}
              title={!selectedCommune ? "Seleccione una comuna primero" : "Convertir comuna seleccionada en polígono"}
            >
              <Shapes className="mr-2 h-4 w-4" />
              Convertir Comuna
            </Button>

            <Button
              color="blue"
              size="sm"
              onClick={() => set_open_table_filters(!open_table_filters)}
              className="shadow-md hover:shadow-lg transition-all"
            >
              <Filter className="mr-2 h-4 w-4" />
              {open_table_filters ? "Ocultar Filtros" : "Mostrar Filtros"}
            </Button>
          </div>
        </div>
        <div
          id="map-container"
          className="relative w-full h-[750px] bg-gray-200 dark:bg-gray-700 rounded-lg shadow-md overflow-hidden"
        >
          <Wrapper
            apiKey={GOOGLE_MAPS_API_KEY}
            render={render}
            libraries={GOOGLE_MAPS_LIBRARIES}
            version={GOOGLE_MAPS_VERSION}
          />

          {/* Portals for Map Overlays */}
          {showPolygonForm &&
            tempPolygon &&
            renderMapPortal(
              <div
                className="bg-white rounded-lg shadow-2xl overflow-hidden"
                style={{ maxHeight: "90vh", overflowY: "auto", pointerEvents: "auto" }}
              >
                <PolygonForm
                  polygon={tempPolygon}
                  onSubmit={handleFormSubmit}
                  onClose={handleFormClose}
                  isAdminView={true}
                  selectedCommune={selectedCommune}
                  isFullscreen={isFullscreen}
                />
              </div>,
            )}

          {mapAlert && renderMapPortal(<MapModal content={mapAlert} onClose={() => setMapAlert(null)} />)}
        </div>
      </div>
      <GlossaryModal isOpen={isGlossaryOpen} onClose={() => setIsGlossaryOpen(false)} />
    </div>
  )
}

export default CoveragesMapScreen
